<template>
  <loading-page
    v-if="initialLoading"
    full
  />
  <div
    v-else
    class="table-wrapper providers"
  >
    <div class="d-block d-sm-flex providers-header">
      <div>
        <b-button
          v-if="$can('create', 'providers', 'snitch')"
          variant="bg-primary"
          @click="onCreateProvider()"
        >
          <GjIcon
            name="Plus"
            class="icon-left"
          />
          Create Provider
        </b-button>
      </div>
      <div>
        <b-button
          v-if="$can('update', 'providers', 'snitch')"
          variant="bg-white"
          :disabled="!isSelected || removingProvider"
          class="d-none d-lg-inline action-provider-button"
          @click="onEditClick(selected[0].id)"
        >
          Edit
        </b-button>
        <button-loading
          v-if="$can('delete', 'providers', 'snitch')"
          variant="bg-white"
          :disabled="!isSelected || removingProvider"
          :spinning="removingProvider"
          class="d-none d-lg-inline action-provider-button"
          @click="onDeleteClick(selected[0].id)"
        >
          Delete
        </button-loading>
      </div>
      <button-loading
        variant="bg-white"
        class="mt-1 mt-sm-0 ml-auto mr-0"
        :spinning="loading"
        :disabled="loading"
        @click="getProviders()"
      >
        <GjIcon
          class="icon-left"
          name="Refresh"
        />
        Refresh
      </button-loading>
    </div>
    <!-- DESKTOP -->
    <div class="default-table position-relative d-none d-lg-block">
      <b-table
        ref="table"
        :items="providers"
        :fields="fields"
        select-mode="single"
        show-empty
        selectable
        responsive
        :class="{ loaded: !loading }"
        :busy="loading || removingProvider"
        @row-selected="onRowSelected"
      >
        <template #cell(selected)="{ rowSelected }">
          <select-button
            :selected="rowSelected"
            class="d-flex justify-content-center"
          />
        </template>

        <template #cell(name)="data">
          <a
            v-if="$can('get', 'providers', 'snitch')"
            class="link"
            @click="onDetailsClick(data.item.id)"
          >
            {{ data.value }}
          </a>
          <span v-else>
            {{ data.value }}
          </span>
        </template>

        <template #cell(type)="data">
          <b-avatar
            v-b-tooltip.hover
            size="42px"
            class="provider-type-badge pull-up"
            :title="changeType(data.item.type)"
            :src="changeTypeSrc(data.item.type)"
          />
          {{ changeType(data.item.type) }}
        </template>

        <template #cell(updateDate)="data">
          {{ formatOffsetDateTime(data.value) }}
        </template>

        <template #empty>
          <div
            v-if="loading"
            class="loading-table text-center py-1"
          >
            <b-spinner variant="primary" />
          </div>
          <template v-else>
            <no-data
              no-data-text="No Providers have been added yet..."
              create-text="+ Create Provider"
              access-page="Providers"
              icon="Boot"
              :has-permissions="hasProviderPermissions"
              @action="onCreateProvider()"
            />
          </template>
        </template>
      </b-table>
      <table-busy v-if="loading && providers.length > 0" />
      <div
        v-if="providers.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
    <!-- MOBILE -->
    <div class="position-relative d-lg-none mt-2">
      <div :class="{ 'opacity-7': loading || removingProvider }">
        <template v-for="provider in providers">
          <card
            :key="provider.id"
            :fields="fields"
            :item="provider"
            class="top-fade-in"
          >
            <template #options>
              <b-dropdown
                id="dropdown-1"
                right
                variant="white"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'providers', 'snitch')"
                  @click="onEditClick(provider.id)"
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('delete', 'providers', 'snitch')"
                  @click="onDeleteClick(provider.id)"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>

            <template #name>
              <a
                v-if="$can('get', 'providers', 'snitch')"
                class="link"
                @click="onDetailsClick(provider.id)"
              >
                {{ provider.name }}
              </a>
              <span v-else>
                {{ provider.name }}
              </span>
            </template>

            <template #type>
              <b-avatar
                v-b-tooltip.hover
                size="42px"
                class="provider-type-badge pull-up"
                :title="changeType(provider.type)"
                :src="changeTypeSrc(provider.type)"
              />
              {{ changeType(provider.type) }}
            </template>

            <template #updateDate>
              {{ formatOffsetDateTime(provider.updateDate) }}
            </template>
          </card>
        </template>
      </div>
      <div
        v-if="loading || removingProvider"
        class="mobile-loading"
      >
        <table-busy />
      </div>
      <template v-if="!loading && providers.length < 1">
        <paper-simple class="top-fade-in">
          <no-data
            class="text-center"
            no-data-text="No providers have been added yet..."
            create-text="+ Create Provider"
            access-page="Providers"
            icon="Notifications"
            :has-permissions="hasProviderPermissions"
            @action="onCreateProvider()"
          />
        </paper-simple>
      </template>
      <div
        v-if="providers.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
    <create-form
      v-if="showCreateForm"
      @close="showCreateForm = false"
      @onRefresh="addProvider"
    />
    <provider-details
      v-if="showProviderDetails"
      :provider-id="providerId"
      @close="showProviderDetails = false"
      @editProvider="editProvider"
    />
    <edit-form
      v-if="showEditForm"
      :provider-id="providerId"
      details
      @close="showEditForm = false"
      @onRefresh="updateProvider"
    />
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'
import CreateForm from './create/Create.vue'
import EditForm from './edit/Edit.vue'
import ProviderDetails from './details/ProviderDetails.vue'

export default {
  name: 'Providers',
  components: {
    Card,
    CreateForm,
    EditForm,
    ProviderDetails,
  },
  data() {
    return {
      initialLoading: true,
      providerId: 0,
      showEditForm: false,
      showProviderDetails: false,
      showCreateForm: false,
      selected: [],
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'name', isPrimary: true },
        { key: 'type', label: 'Type', isPrimary: true },
        { key: 'webhookUrl', label: 'Webhook Url' },
        { key: 'updateDate', label: 'Last Modified' },
      ],
      hasProviderPermissions: true,
      /* eslint-disable global-require */
      googleChat: require('@/assets/svg/icons/google-chat.svg'),
      slack: require('@/assets/svg/icons/slack.svg'),
      discord: require('@/assets/svg/icons/discord.svg'),
      /* eslint-enable global-require */
    }
  },
  computed: {
    loading() {
      return this.$store.state.providers.fetchingProviders
    },
    providers() {
      return this.$store.state.providers.providers
    },
    removingProvider() {
      return this.$store.state.providers.removingProvider
    },
    isSelected() {
      return this.selected[0]
    },
    totalCount() {
      return this.resultInfo?.totalCount
    },
    resultInfo() {
      return this.$store.state.providers.providersResultInfo
    },
    pageSize() {
      return this.resultInfo?.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentPage: {
      get() {
        return this.$store.getters['providers/currentPage']
      },
      set(value) {
        this.$store.commit('providers/SET_CURRENT_PAGE', value)
      },
    },
  },
  created() {
    this.getProviders()
  },
  methods: {
    onRowSelected(item) {
      this.selected = item
    },
    onCreateProvider() {
      this.showCreateForm = true
    },
    onDetailsClick(providerId) {
      this.providerId = providerId
      this.showProviderDetails = true
    },
    onEditClick(providerId) {
      this.providerId = providerId
      this.showEditForm = true
    },
    editProvider(providerId) {
      this.providerId = providerId
      this.showProviderDetails = false
      this.showEditForm = true
    },
    addProvider() {
      this.getProviders()
      this.showCreateForm = false
    },
    updateProvider() {
      this.getProviders()
      this.showEditForm = false
    },
    changeType(type) {
      if (type === 0) {
        return 'Google Chat'
      }
      if (type === 1) {
        return 'Slack'
      }
      if (type === 2) {
        return 'Discord'
      }
      return null
    },
    changeTypeSrc(type) {
      if (type === 0) {
        return this.googleChat
      }
      if (type === 1) {
        return this.slack
      }
      if (type === 2) {
        return this.discord
      }
      return null
    },
    onDeleteClick(providerId) {
      this.confirmDelete(
        'Delete Provider',
        'Are you sure you want to delete this provider?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('providers/removeProvider', providerId)
            .then(() => {
              this.successToast('Provider was removed!')
              this.selected = []
              if (this.providers.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.getProviders()
              }
            })
            .catch(error => {
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting provider.')
            })
        }
      })
    },
    getProviders() {
      this.$store
        .dispatch('providers/fetchProviders', {
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 426 || status === 428) {
            this.$store.commit('observability/SET_SERVICE_UNAVAILABLE', { serviceUnavailable: true, serviceRequested: status === 428 })
            return
          }
          if (status === 403) {
            this.hasProviderPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching providers!',
          )
        })
        .finally(() => { this.initialLoading = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.providers {
  .provider-type-badge {
    background-color: #fff !important;
    border: 1px solid #f6f7f9;
  }
  ::v-deep {
    td {
      padding: 7px !important;
    }
  }
}
</style>
